<template>
  <div class="base-input">
    <label
      v-if="label"
      :for="inputId"
      class="base-input__label"
      :class="{ 'base-input__label--required': required }"
    >
      {{ label }}
    </label>

    <div class="base-input__wrapper">
      <div v-if="$slots.prefix" class="base-input__prefix">
        <slot name="prefix" />
      </div>

      <input
        :id="inputId"
        :value="modelValue"
        :type="type"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :autocomplete="autocomplete"
        :class="[
          'base-input__field',
          {
            'base-input__field--error': error || errorMessage,
            'base-input__field--disabled': disabled,
            'base-input__field--readonly': readonly,
            'base-input__field--with-prefix': $slots.prefix,
            'base-input__field--with-suffix': $slots.suffix
          }
        ]"
        @input="handleInput"
        @blur="handleBlur"
        @focus="handleFocus"
        @keyup.enter="$emit('enter', $event)"
      />

      <div v-if="$slots.suffix" class="base-input__suffix">
        <slot name="suffix" />
      </div>
    </div>

    <div v-if="errorMessage || $slots.error" class="base-input__error">
      <slot name="error">{{ errorMessage }}</slot>
    </div>

    <div v-else-if="helpText || $slots.help" class="base-input__help">
      <slot name="help">{{ helpText }}</slot>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed } from 'vue'

let inputIdCounter = 0

export default defineComponent({
  name: 'BaseInput',
  emits: ['update:modelValue', 'input', 'blur', 'focus', 'enter'],
  props: {
    modelValue: {
      type: [String, Number],
      default: ''
    },
    type: {
      type: String,
      default: 'text',
      validator: (value) => ['text', 'email', 'password', 'number', 'tel', 'url', 'search'].includes(value)
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    helpText: {
      type: String,
      default: ''
    },
    errorMessage: {
      type: String,
      default: ''
    },
    error: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    autocomplete: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      default: ''
    }
  },
  setup(props, { emit }) {
    const inputId = computed(() => {
      return props.id || `base-input-${++inputIdCounter}`
    })

    const handleInput = (event) => {
      const value = props.type === 'number' ?
        (event.target.value ? Number(event.target.value) : '') :
        event.target.value

      emit('update:modelValue', value)
      emit('input', event)
    }

    const handleBlur = (event) => {
      emit('blur', event)
    }

    const handleFocus = (event) => {
      emit('focus', event)
    }

    return {
      inputId,
      handleInput,
      handleBlur,
      handleFocus
    }
  }
})
</script>

<style scoped>
.base-input {
  width: 100%;
}

.base-input__label {
  display: block;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-700);
  margin-bottom: var(--space-2);
}

.base-input__label--required::after {
  content: ' *';
  color: var(--color-error);
}

.base-input__wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.base-input__field {
  width: 100%;
  padding: var(--space-3) var(--space-4);
  font-size: var(--font-size-base);
  font-family: var(--font-family-base);
  line-height: 1.5;
  color: var(--color-gray-900);
  background-color: var(--color-white);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  transition: all var(--transition-fast);
  outline: none;
}

.base-input__field::placeholder {
  color: var(--color-gray-400);
}

.base-input__field:focus {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-primary-light);
}

.base-input__field:hover:not(:focus):not(.base-input__field--disabled) {
  border-color: var(--color-gray-400);
}

/* Error State */
.base-input__field--error {
  border-color: var(--color-error);
  background-color: #FEF2F2;
}

.base-input__field--error:focus {
  border-color: var(--color-error);
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
}

/* Disabled State */
.base-input__field--disabled {
  background-color: var(--color-gray-100);
  color: var(--color-gray-500);
  cursor: not-allowed;
  opacity: 0.7;
}

.base-input__field--disabled::placeholder {
  color: var(--color-gray-400);
}

/* Readonly State */
.base-input__field--readonly {
  background-color: var(--color-gray-50);
  cursor: default;
}

/* Error Message */
.base-input__error {
  margin-top: var(--space-2);
  font-size: var(--font-size-sm);
  color: var(--color-error);
  line-height: 1.4;
}

/* Help Text */
.base-input__help {
  margin-top: var(--space-2);
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
  line-height: 1.4;
}

/* Prefix/Suffix Slots */
.base-input__prefix {
  position: absolute;
  left: var(--space-3);
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-gray-500);
  font-size: var(--font-size-sm);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.base-input__suffix {
  position: absolute;
  right: var(--space-3);
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-gray-500);
  font-size: var(--font-size-sm);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
}

.base-input__field--with-prefix {
  padding-left: calc(var(--space-8) + var(--space-2));
}

.base-input__field--with-suffix {
  padding-right: calc(var(--space-8) + var(--space-2));
}

/* Responsive adjustments */
@media (max-width: 767px) {
  .base-input__field {
    font-size: 16px; /* Prevents iOS zoom */
    padding: var(--space-3);
  }
}

/* Number input specific styles */
.base-input__field[type="number"] {
  -moz-appearance: textfield;
}

.base-input__field[type="number"]::-webkit-outer-spin-button,
.base-input__field[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Password input styles */
.base-input__field[type="password"] {
  font-family: 'Courier New', monospace;
}

/* Search input styles */
.base-input__field[type="search"] {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

.base-input__field[type="search"]::-webkit-search-cancel-button {
  -webkit-appearance: none;
  appearance: none;
}
</style>